import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import {Select} from '../../../shared/Select'
import {Spinner} from '../../../../components/loader-spinner'
import {AccountEditModal} from '../components/AccounEditModal'
import {locationToFormValues} from '../AllAccounts/AllAccountsFormMapping'

export const TIER_OPTIONS = [
  {label: 'Keap Pro Sandbox', value: 'keap_pro-sandbox-5-thryv-m2m'},
  {
    label: 'Keap Ultimate Sandbox',
    value: 'keap_max-classic-sandbox-5-thryv-m2m',
  },
  {label: 'Keap Max Sandbox', value: 'keap_max-sandbox-5-thryv-m2m'},
]

// Fields not relevant for a sandbox child account
const SANDBOX_HIDDEN_FIELDS = [
  'salesRepOrPartner',
  'salesRepInfo',
  'xpCode',
  'overrideSST',
  'overrideRepInfo',
  'locationName',
  'salesforceEmail',
  'abn',
  'nzbn',
  'leadId',
  'internalAccount',
  'billTo',
  'telephone',
]

function buildFormValues(mode, parent, sandbox) {
  if (mode === 'edit') {
    return locationToFormValues(sandbox)
  }
  const base = locationToFormValues(null)
  if (parent) {
    const parentName = parent?.company || parent?.companyName || ''
    base.businessName = parentName ? `${parentName} Sandbox` : ''
    base.email = parent?.email || parent?.accountEmail || ''
    base.firstName = parent?.firstName || ''
    base.lastName = parent?.lastName || ''
    base.mobile = parent?.address?.phone || ''
    base.address = parent?.address?.street1 || parent?.address?.street || ''
    base.city = parent?.address?.city || ''
    base.state = parent?.address?.region || parent?.address?.state || ''
    base.postCode = parent?.address?.postalCode || ''
    base.country =
      parent?.address?.country || parent?.country || parent?.countryCode || ''
  }
  return base
}

export const SandboxForm = forwardRef(function SandboxForm(
  {mode = 'create', parent, sandbox, initialTier, loading = false, onSubmit},
  ref,
) {
  const [tierPlanCode, setTierPlanCode] = useState(
    mode === 'edit'
      ? initialTier ?? TIER_OPTIONS[0].value
      : TIER_OPTIONS[0].value,
  )
  const [tierError, setTierError] = useState(null)
  const valuesRef = useRef(null)

  const accountModalValues = useMemo(
    () => buildFormValues(mode, parent, sandbox),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, sandbox, parent],
  )

  // Sync tier when fresh edit data arrives
  useEffect(() => {
    if (mode !== 'edit') return
    setTierPlanCode(initialTier ?? TIER_OPTIONS[0].value)
  }, [initialTier, mode])

  useImperativeHandle(ref, () => ({
    submit() {
      if (!tierPlanCode) {
        setTierError('Required')
        return
      }
      const vals = valuesRef.current || accountModalValues
      const country = vals.country || 'US'
      onSubmit({
        tierPlanCode,
        firstName: vals.firstName || '',
        lastName: vals.lastName || '',
        company: vals.businessName || '',
        email: vals.email || '',
        phone: vals.mobile || undefined,
        country,
        address: {
          street: vals.address || undefined,
          city: vals.city || undefined,
          state: vals.state || undefined,
          zipCode: vals.postCode || undefined,
          country,
        },
      })
    },
  }))

  if (loading) {
    return (
      <div
        style={{display: 'flex', justifyContent: 'center', padding: '40px 0'}}
      >
        <Spinner />
      </div>
    )
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
      <Select
        label="Sandbox Tier"
        required
        options={TIER_OPTIONS}
        value={tierPlanCode}
        onChange={e => {
          setTierPlanCode(e.target.value)
          if (tierError) setTierError(null)
        }}
        errorText={tierError}
        placeholder="Select tier"
        variant="form"
      />
      <AccountEditModal
        type="add-location"
        account={mode === 'edit' ? sandbox : null}
        valuesOverride={accountModalValues}
        onValuesChange={vals => {
          valuesRef.current = vals
        }}
        additionalHiddenFields={SANDBOX_HIDDEN_FIELDS}
        hideTypeBadge
      />
    </div>
  )
})
